import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLessonById } from "../data/index.ts";
import { gameConfig, xpConfiguration } from "../data/ui/uiConfig.ts";
import { getUserData, loseHeart } from "../utils/userStorage.ts";
import FinishLessonPage from "./FinishLessonPage.tsx";
import QuestionComposeSentencesPage from "./QuestionComposeSentencesPage.tsx";
import QuestionFillInTheBlankPage from "./QuestionFillInTheBlankPage.tsx";
import QuestionMatchingWordsPage from "./QuestionMatchingWordsPage.tsx";
import QuestionVocabIntroPage from "./QuestionVocabIntroPage.tsx";
import QuestionWordTranslationPage from "./QuestionWordTranslationPage.tsx";

// Utility to shuffle array
function shuffle<T>(array: T[]): T[] {
  return array
    .map((v) => [Math.random(), v] as [number, T])
    .sort((a, b) => a[0] - b[0])
    .map((a) => a[1]);
}

// Type definitions for question components
interface QuestionComponentConfig {
  type: string;
  component: React.ComponentType<any>;
  questionConfig?: any;
  vocabularyBank?: any[];
}

const QUESTION_COMPONENTS: QuestionComponentConfig[] = [
  { type: "word-translation", component: QuestionWordTranslationPage },
  { type: "compose-sentences", component: QuestionComposeSentencesPage },
  { type: "matching-words", component: QuestionMatchingWordsPage },
  { type: "fill-in-the-blank", component: QuestionFillInTheBlankPage },
];

// Vocab intro is always first and not shuffled
const VOCAB_INTRO_COMPONENT: QuestionComponentConfig = { type: "vocab-intro", component: QuestionVocabIntroPage };

const QuestionMainPage = () => {
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Listen for localStorage changes to keep darkMode in sync
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const location = useLocation();

  // Get lessonId from URL params
  const lessonId = new URLSearchParams(location.search).get('lessonId');

  // Get lesson data based on lessonId
  const lessonData = useMemo(() => {
    if (lessonId) {
      const lesson = getLessonById(lessonId);
      console.log('🎓 Lesson ID:', lessonId);
      console.log('📚 Lesson Data:', lesson);
      console.log('❓ Questions Count:', lesson?.questions?.length || 0);
      console.log('📖 Vocabulary Count:', lesson?.vocab?.length || 0);
      return lesson;
    }
    return null;
  }, [lessonId]);

  // Initialize hearts from user data
  const [hearts, setHearts] = useState(() => {
    const userData = getUserData();
    return userData?.meta?.heart ?? gameConfig.defaultHearts; // Use nullish coalescing to allow 0
  });

  // Listen for heart changes from userStorage
  useEffect(() => {
    const handleUserDataChange = () => {
      const userData = getUserData();
      const currentHearts = userData?.meta?.heart ?? gameConfig.defaultHearts; // Use nullish coalescing to allow 0
      setHearts(currentHearts);
    };

    // Listen for custom userDataChanged event
    window.addEventListener('userDataChanged', handleUserDataChange);

    return () => {
      window.removeEventListener('userDataChanged', handleUserDataChange);
    };
  }, []);

  // Function to handle incorrect answers
  const handleIncorrectAnswer = () => {
    console.log('❤️ Incorrect answer! Losing a heart...');
    const success = loseHeart(1);
    if (success) {
      const userData = getUserData();
      const newHeartCount = userData?.meta?.heart || 0;
      setHearts(newHeartCount);
      console.log(`💔 Heart lost! Remaining hearts: ${newHeartCount}`);

      // Check if hearts are depleted
      if (newHeartCount <= 0) {
        console.log('💀 No hearts left! Game over logic could go here...');
        // TODO: Implement game over logic
      }
    }
  };
  const [isFinished, setIsFinished] = useState(false);
  const [finishData, setFinishData] = useState({
    xp: 0,
    time: 0,
    perfect: true,
    accuracy: 0,
  });
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [startTime] = useState(Date.now());

  const questionOrder = useMemo(() => {
    // If we have lesson data with questions, use those
    if (lessonData && lessonData.questions && lessonData.questions.length > 0) {
      console.log('🎯 Processing questions from lesson data...');

      // Separate vocab-intro and other questions while preserving order
      const vocabIntroQuestions: any[] = [];
      const otherQuestions: any[] = [];

      lessonData.questions.forEach((question: any) => {
        if (question.type === "vocab-intro") {
          vocabIntroQuestions.push(question);
        } else {
          otherQuestions.push(question);
        }
      });

      // Create ordered component list with vocab-intro first, then others in original order
      const orderedComponents: QuestionComponentConfig[] = [];

      // Add vocab intro questions first
      vocabIntroQuestions.forEach((question: any) => {
        orderedComponents.push({
          type: "vocab-intro",
          component: QuestionVocabIntroPage,
          questionConfig: question,
          vocabularyBank: lessonData.vocab || []
        });
      });

      // Add other questions in their original order
      otherQuestions.forEach((question: any) => {
        let component;
        switch (question.type) {
          case "word-translation":
            component = QuestionWordTranslationPage;
            break;
          case "fill-in-the-blank":
            component = QuestionFillInTheBlankPage;
            break;
          case "compose-sentences":
            component = QuestionComposeSentencesPage;
            break;
          case "matching-words":
            component = QuestionMatchingWordsPage;
            break;
          default:
            return;
        }

        orderedComponents.push({
          type: question.type,
          component,
          questionConfig: question,
          vocabularyBank: lessonData.vocab || []
        });
      });

      return orderedComponents;
    }

    // Fallback to original logic if no lesson data
    return [VOCAB_INTRO_COMPONENT, ...shuffle([...QUESTION_COMPONENTS])];
  }, [lessonData]);
  const [currentPage, setCurrentPage] = useState(0);

  // Progress state: 0 = empty, 1 = full
  const [progress, setProgress] = useState(0);

  // XP per type, max 10 each
  const [xpByType, setXpByType] = useState<{ [type: string]: number }>(
    xpConfiguration.defaultXpByType
  );

  // Called after answer is validated (after "Periksa" clicked)
  const handleValidate = () => {
    setProgress((currentPage + 1) / questionOrder.length);
  };

  // Called from each question component when user finishes that question
  const handleAnswer = (
    xpEarned: number,
    type: string,
    correct: number,
    total: number
  ) => {
    if (xpEarned > 0) {
      setXpByType((prev) => ({
        ...prev,
        [type]: Math.min(xpConfiguration.maxXpPerType, xpEarned),
      }));
    }
    setCorrectCount((prev) => prev + correct);
    setTotalCount((prev) => prev + total);
  };

  const handleNext = () => {
    if (currentPage < questionOrder.length - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      // Calculate time
      const finishTime = Date.now();

      // Calculate accuracy, max 100%
      const rawAccuracy =
        totalCount === 0 ? 0 : (correctCount / totalCount) * 100;
      const accuracy = Math.min(gameConfig.maxAccuracy, Math.round(rawAccuracy));

      const totalXp = Object.values(xpByType).reduce((a, b) => a + b, 0);

      setFinishData({
        xp: totalXp,
        accuracy,
        time: Math.floor((finishTime - startTime) / 1000),
        perfect: correctCount === totalCount && totalCount > 0,
      });
      setIsFinished(true);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      setProgress((currentPage - 1) / questionOrder.length);
    }
  };

  const handleRestart = () => {
    setCurrentPage(0);
    setHearts(gameConfig.defaultHearts);
    setProgress(0);
    setIsFinished(false);
    setCorrectCount(0);
    setTotalCount(0);
    setXpByType(xpConfiguration.defaultXpByType);
  };

  const CurrentComponent = questionOrder[currentPage]?.component;
  const currentType = questionOrder[currentPage]?.type;
  const currentQuestionConfig = questionOrder[currentPage]?.questionConfig;
  const currentVocabularyBank = questionOrder[currentPage]?.vocabularyBank;

  // Safety check
  if (!CurrentComponent || !currentType) {
    return <div>Loading...</div>;
  }

  if (isFinished) {
    return (
      <FinishLessonPage
        xp={finishData.xp}
        time={finishData.time}
        perfect={finishData.perfect}
        correctCount={correctCount}
        totalCount={totalCount}
        lessonId={lessonId || undefined}
        darkMode={darkMode}
      />
    );
  }

  return (
    <div className="font-sans">
      <CurrentComponent
        hearts={hearts}
        onNext={handleNext}
        onBack={handleBack}
        onRestart={handleRestart}
        progress={progress}
        isLast={currentPage === questionOrder.length - 1}
        onValidate={handleValidate}
        onAnswer={(xp: number, correct: number, total: number) => {
          handleAnswer(xp, currentType, correct, total);
        }}
        onIncorrectAnswer={handleIncorrectAnswer}
        darkMode={darkMode}
        questionConfig={currentQuestionConfig}
        vocabularyBank={currentVocabularyBank}
        lessonData={lessonData}
      />
    </div>
  );
};

export default QuestionMainPage;
